import { inject, Injectable, isDevMode, PLATFORM_ID } from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { VESTA_WINDOW } from '../tokens/vesta-window.token';
import { ATTRIBUTE_REGEX } from '../utils/script-attributes.utils';
import { VestaResponse } from '../models/vesta-response.model';
import { VESTA_CONFIG } from '../tokens/vesta-config.token';

@Injectable({ providedIn: 'root' })
export class VestaRuntimeService {
  private initialized = false;
  private document = inject(DOCUMENT);
  private platformId = inject(PLATFORM_ID);
  private win = inject(VESTA_WINDOW);
  private config = inject(VESTA_CONFIG);

  public init(): void {
    if (!isPlatformBrowser(this.platformId) || this.initialized) {
      return;
    }
    this.initialized = true;
    this.exposeVestaManager();
    this.loadEnvironmentAndScript();
  }

  public whenReady(cb: () => void): void {
    queueMicrotask(cb);
  }

  public destroy(): void {
    this.document.querySelectorAll('script[data-vesta]').forEach((s) => s.remove());
    this.initialized = false;
  }

  private appendScript(url?: string, id?: string, optionString = ''): void {
    if (!url) return;
    const key = id ?? url;
    if (this.document.querySelector(`script[data-vesta="${key}"]`)) {
      return;
    }
    const script = this.document.createElement('script');
    script.type = 'text/javascript';
    script.src = url;
    script.setAttribute('data-vesta', key);
    if (optionString) {
      ATTRIBUTE_REGEX.lastIndex = 0;
      let match;
      while ((match = ATTRIBUTE_REGEX.exec(optionString)) !== null) {
        script.setAttribute(match[1], match[2]);
      }
    }
    this.document.head.appendChild(script);
  }

  private loadEnvironmentAndScript(): void {
    fetch(this.config.envPath)
      .then((res) => (res.ok ? res.text() : null))
      .then((baseUrl) => {
        if (isDevMode() && this.config.isVestaDevMode) {
          this.appendScript('http://localhost:8080/main.js', '3d', 'type="module"');
        } else if (baseUrl) {
          const clean = baseUrl.replace(/\/$/, '');
          this.appendScript(`${clean}/${this.config.scriptName}`, '3d');
        } else {
          this.appendScript(this.config.defaultPath, '3d');
        }
      })
      .catch(() => {
        this.appendScript(this.config.defaultPath, '3d');
      });
  }

  private exposeVestaManager(): void {
    this.win.callVesta3DManager = (url: string): Promise<void> => {
      return fetch(url)
        .then((res) => {
          if (!res.ok) {
            throw new Error(`HTTP ${res.status}`);
          }
          return res.json() as Promise<VestaResponse>;
        })
        .then((json) => {
          json.data?.links?.forEach((link) => this.appendScript(link));
          json.data?.scripts?.forEach((s) => {
            if (s.resource?.url) {
              this.appendScript(s.resource.url, s.option);
            }
          });
        })
        .catch((err) => {
          console.error('[Vesta3DManager]', err);
        });
    };
  }
}
